import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, Col, Container, Row, Button, Label, Input, FormFeedback, Spinner } from 'reactstrap';
import { createSelector } from 'reselect';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { questionListResetFlag, saveStdInfo as onSaveStdInfo } from '../../../slices/exam/thunk';
import { getRiderRegistrationData, riderUpdateLanguages, riderGetInstructions } from '../../../helpers/custom_helper/backend_helper';
import ParticlesAuth from "../../../pages/AuthenticationInner/ParticlesAuth";
import { Link } from "react-router-dom";
import { api } from '../../../config';

const getRegistrationEntryPath = (courseShortName) => {
  const stored = localStorage.getItem('registrationEntryPath');
  if (stored) return stored;
  return courseShortName === 'MCD' ? '/rider-register' : '/student-register';
};

const RiderExamStart = () => {
  document.title = "Start Exam | Theory Practice";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const mediaSource = api.IMAGE_URL;

  const [storedData, setStoredData] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);
  const [selectedQLang, setSelectedQLang] = useState('en');
  const [selectedAudioLang, setSelectedAudioLang] = useState('');
  const [errors, setErrors] = useState({});
  const [currentInstructions, setCurrentInstructions] = useState("");

  const selectExamState = (state) => state.ExamReducer;
  const examProperties = createSelector(
    selectExamState,
    (data) => ({
      stdInfo: data.stdInfo,
    })
  );
  const { stdInfo } = useSelector(examProperties);

  const [examData, setExamData] = useState(null);

  useEffect(() => {
    const dataFromLocalStorage = localStorage.getItem('apiData');
    setStoredData(dataFromLocalStorage ? JSON.parse(dataFromLocalStorage) : null);

    let currentExamData = null;
    if (stdInfo && stdInfo.examId) {
      currentExamData = stdInfo;
    } else {
      const stored = localStorage.getItem('StudentInformation');
      if (stored) {
        currentExamData = JSON.parse(stored);
      } else {
        navigate(getRegistrationEntryPath());
        return;
      }
    }
    
    setExamData(currentExamData);
    if (currentExamData.qLangShortName) setSelectedQLang(currentExamData.qLangShortName);
    setCurrentInstructions(currentExamData.exam_instructions || "");
    setSelectedAudioLang('');

    fetchLanguages();
    dispatch(questionListResetFlag());
  }, []);

  // Fetch instructions when selectedQLang changes
  useEffect(() => {
    if (examData && examData.courseShortName && selectedQLang) {
      fetchInstructions(examData.courseShortName, selectedQLang);
    }
  }, [selectedQLang, examData]);

  const fetchLanguages = async () => {
    try {
      const response = await getRiderRegistrationData();
      if (response && response.success) {
        setLanguages(response.data.languages || []);
      }
    } catch (error) {
      console.error('Error loading languages', error);
    }
  };

  const fetchInstructions = async (courseCode, lang) => {
    setIsLoadingInstructions(true);
    try {
      const response = await riderGetInstructions({ course_code: courseCode, lang: lang });
      if (response && response.success) {
        setCurrentInstructions(response.data.exam_instructions);
      }
    } catch (error) {
      console.error('Error fetching instructions', error);
    } finally {
      setIsLoadingInstructions(false);
    }
  };

  const handleStartExam = async () => {
    if (!selectedQLang) {
      setErrors({
        q_lang: 'Please select question language'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await riderUpdateLanguages({
        exam_id: examData.examId,
        q_lang: selectedQLang,
        audio_lang: selectedAudioLang || null
      });

      if (response && response.success) {
        const updatedExamData = {
          ...examData,
          qLangShortName: response.data.qLangShortName,
          qLangFullName: response.data.qLangFullName,
          audioLangShortName: response.data.audioLangShortName,
          audioLangFullName: response.data.audioLangFullName,
          direction: response.data.direction,
          instructions: response.data.instructions,
          exam_instructions: response.data.exam_instructions,
        };

        window.localStorage.setItem("StudentInformation", JSON.stringify(updatedExamData));
        dispatch(onSaveStdInfo(updatedExamData));
        
        navigate('/theory-practice');
      } else {
        toast.error(response?.message || 'Failed to update languages');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format instructions as bullets
  const renderFormattedInstructions = (content) => {
    if (!content) return null;

    const preCleaned = content
      .replace(/<\/p>|<\/div>|<br\s*\/?>/gi, '\n') 
      .replace(/<[^>]*>?/gm, '');

    const rawSegments = preCleaned.split(/[.?!]|\n/);
    
    const sentences = rawSegments
      .map(s => s.trim())
      .filter(s => s.length > 3);

    return (
      <ul className="mb-0 ps-3 custom-instructions-list">
        {sentences.map((sentence, index) => {
          const marker = sentence.match(/[.?!]$/) ? '' : '.';
          return (
            <li key={index} className="mb-2 text-muted" style={{ fontSize: "14px", lineHeight: "1.5" }}>
              {sentence}{marker}
            </li>
          );
        })}
      </ul>
    );
  };

  if (!examData) return null;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={storedData && storedData.logo ? mediaSource + storedData.logo : ""} alt="" height="75" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">{storedData && storedData.title}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={10} lg={8} xl={7}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Ready to start?</h5>
                      <p className="text-muted">Review instructions and select your preferences</p>
                    </div>

                    <div className="p-2 mt-4">
                      <div className="bg-light p-3 rounded mb-4">
                        <Row className="align-items-center mb-2">
                          <Col sm={4} className="text-muted fw-medium">Student Name:</Col>
                          <Col sm={8} className="fw-bold">{examData.stdName}</Col>
                        </Row>
                        <Row className="align-items-center mb-2">
                          <Col sm={4} className="text-muted fw-medium">Traffic ID:</Col>
                          <Col sm={8} className="fw-bold">{examData.trafficId}</Col>
                        </Row>
                        <Row className="align-items-center">
                          <Col sm={4} className="text-muted fw-medium">Course:</Col>
                          <Col sm={8} className="fw-bold text-success">{examData.courseName}</Col>
                        </Row>
                      </div>

                      {/* Instructions Section (Moved Above Languages) */}
                      <div className="mb-4 p-3 bg-light rounded text-muted border-start border-4 border-info position-relative">
                        {isLoadingInstructions && (
                          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-light opacity-75 rounded">
                            <Spinner size="sm" color="primary" />
                          </div>
                        )}
                        <h6 className="fw-bold mb-3 text-primary">
                          <i className="ri-information-line align-bottom me-1"></i> Exam Instructions:
                        </h6>
                        {currentInstructions ? (
                          renderFormattedInstructions(currentInstructions)
                        ) : (
                          <p className="mb-0 italic">No instructions available for this language.</p>
                        )}
                      </div>

                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="q_lang" className="form-label">Question Language <span className="text-danger">*</span></Label>
                            <Input
                              type="select"
                              id="q_lang"
                              value={selectedQLang}
                              onChange={(e) => {
                                setSelectedQLang(e.target.value);
                                setErrors(prev => ({ ...prev, q_lang: '' }));
                              }}
                              invalid={!!errors.q_lang}
                            >
                              <option value="">Select Language</option>
                              {languages.map((lang) => (
                                <option key={lang.id} value={lang.lang_short}>
                                  {lang.lang}
                                </option>
                              ))}
                            </Input>
                            {errors.q_lang && <FormFeedback>{errors.q_lang}</FormFeedback>}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="audio_lang" className="form-label">Audio Language (Optional)</Label>
                            <Input
                              type="select"
                              id="audio_lang"
                              value={selectedAudioLang}
                              onChange={(e) => {
                                setSelectedAudioLang(e.target.value);
                              }}
                            >
                              <option value="">None / No Audio</option>
                              {languages.map((lang) => (
                                <option key={lang.id} value={lang.lang_short}>
                                  {lang.lang}
                                </option>
                              ))}
                            </Input>
                          </div>
                        </Col>
                      </Row>

                      <div className="mt-4 d-flex gap-3">
                        <Button color="light" className="w-100" onClick={() => navigate(getRegistrationEntryPath(examData?.courseShortName))}>
                          Back
                        </Button>
                        <Button color="success" className="w-100" onClick={handleStartExam} disabled={isSubmitting}>
                          {isSubmitting ? <Spinner size="sm" className="me-2"> Loading... </Spinner> : null}
                          Start Exam
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
      <ToastContainer autoClose={2000} limit={1} />
    </React.Fragment>
  );
};

export default RiderExamStart;
